// "Continuar con Google / Apple / Facebook" (documento 20, decisiones D48 y D51). La tienda no habla con el proveedor:
// manda el navegador a la API (`/v1/tienda/publico/<proveedor>/iniciar`), que va al proveedor y vuelve a
// `/entrar/<proveedor>#c=<código de un uso>`. Para que ese código no sirva en otro celular, antes de ir se guarda acá un
// secreto al azar y viaja solo su SHA-256; al volver se canjea con el secreto.
// (El archivo se llama google.ts porque así empezó, con la decisión D48.)
import { NOMBRE_PROVEEDOR, type Proveedor, PROVEEDORES, rutaVolverTienda } from '../compartido';
import { borrar, guardar, leer } from './almacen';
import { claveLocal, SLUG } from './tienda-actual';

export { NOMBRE_PROVEEDOR, PROVEEDORES, type Proveedor };

const CLAVE_SECRETO = claveLocal('google');
/** El ingreso dura 10 minutos del lado de la API: un secreto más viejo ya no sirve. */
const VIGENCIA_MS = 15 * 60_000;

interface SecretoGuardado {
  secreto: string;
  creado: number;
}

const aBase64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export function secretoAlAzar(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return aBase64url(b);
}

/** SHA-256 del secreto en base64url (lo que la API guarda y compara al canjear). */
export async function desafioDe(secreto: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secreto));
  return aBase64url(new Uint8Array(h));
}

/** Prepara la ida: guarda un secreto nuevo y devuelve su desafío. */
export async function nuevoDesafio(): Promise<string> {
  const secreto = secretoAlAzar();
  guardar(CLAVE_SECRETO, { secreto, creado: Date.now() } satisfies SecretoGuardado);
  return desafioDe(secreto);
}

/** El secreto de la ida (se usa una sola vez). Null si no hay, venció o se abrió en otro navegador. */
export function tomarSecreto(ahora = Date.now()): string | null {
  const s = leer<SecretoGuardado>(CLAVE_SECRETO);
  borrar(CLAVE_SECRETO);
  return s?.secreto && ahora - s.creado < VIGENCIA_MS ? s.secreto : null;
}

/** Dirección de la API que manda al proveedor. `volver`: a qué pantalla ir después de entrar. */
export function urlIniciar(proveedor: Proveedor, desafio: string, volver = '/'): string {
  const q = new URLSearchParams({ slug: SLUG, volver: rutaVolverTienda(volver), desafio });
  return `/v1/tienda/publico/${proveedor}/iniciar?${q.toString()}`;
}

/** Lo mismo con Google (nombre de antes). */
export const urlIniciarGoogle = (desafio: string, volver = '/') => urlIniciar('google', desafio, volver);

/** Va al proveedor (deja la tienda: vuelve sola a `/entrar/<proveedor>`). */
export async function irAProveedor(proveedor: Proveedor, volver = '/'): Promise<void> {
  location.assign(urlIniciar(proveedor, await nuevoDesafio(), volver));
}

export type ErrorVueltaGoogle = 'cancelado' | 'google' | 'email_sin_verificar' | 'no_disponible' | 'tienda_cerrada';

export interface VueltaGoogle {
  codigo: string | null;
  error: ErrorVueltaGoogle | null;
  /** Volvió de vincular la cuenta desde "Tu cuenta" (no de entrar). */
  vincular: boolean;
}

/** Lee lo que dejó la API después de "#" (`c`, `error`, `t`) y lo borra de la dirección. */
export function leerVueltaGoogle(hash: string): VueltaGoogle | null {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  const c = p.get('c');
  const error = p.get('error');
  if (!c && !error) return null;
  const errores: readonly string[] = ['cancelado', 'google', 'email_sin_verificar', 'no_disponible', 'tienda_cerrada'];
  return {
    codigo: c && /^[A-Za-z0-9_-]{20,80}$/.test(c) ? c : null,
    error: error ? ((errores.includes(error) ? error : 'google') as ErrorVueltaGoogle) : null,
    vincular: p.get('t') === 'vincular',
  };
}

export function tomarVueltaGoogle(): VueltaGoogle | null {
  if (typeof location === 'undefined') return null;
  const v = leerVueltaGoogle(location.hash);
  if (v) history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  return v;
}

/** El proveedor de la dirección de vuelta (`/entrar/<proveedor>`). */
export function proveedorDeRuta(ruta: string): Proveedor | null {
  const p = /^\/entrar\/([a-z]+)\/?$/.exec(ruta)?.[1];
  return p && (PROVEEDORES as readonly string[]).includes(p) ? (p as Proveedor) : null;
}

/** Lo que se le dice a la persona si la vuelta trajo un error. */
export function mensajeVueltaGoogle(error: ErrorVueltaGoogle, proveedor: Proveedor = 'google'): string {
  const n = NOMBRE_PROVEEDOR[proveedor];
  switch (error) {
    case 'cancelado':
      return `No terminaste de elegir tu cuenta de ${n}. Probá de nuevo cuando quieras.`;
    case 'email_sin_verificar':
      return `Tu cuenta de ${n} no tiene el correo verificado. Verificalo en ${n} o entrá con tu correo y contraseña.`;
    case 'no_disponible':
      return `El ingreso con ${n} no está disponible en esta tienda. Entrá con tu correo y contraseña.`;
    case 'tienda_cerrada':
      return 'La tienda está cerrada por ahora.';
    default:
      return `No pudimos entrar con ${n}. Probá de nuevo.`;
  }
}
